import React, { FC, useEffect, useState, useRef, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { cx } from '@emotion/css';

import { CHILD_ZOOM, MAP_OPTIONS, DEFAULT_CENTER } from '../../../../../constants/ecp-locator';
import { useStyles } from './appointment-map.styles';
import { IAppointmentMapProps } from './appointment-map.models';
import { getMarkerIcon } from '../../../../../utils/ecp-locator/get-icon-from-type';
import { useYandexMapsScript } from '../../../../../hooks/useYandexMapsScripts';
import { initializeYandexMaps, cleanupYandexMaps } from '../../../../../lib/ymap';
import { MapPlaceholder } from '../../../../common/map-placeholder/map-placeholder';
import { v2025PP18603 } from '../../../../../constants/veeva';
import { useMapStyles } from '../../../../../hooks/use-map-styles/use-map-styles';

const YANDEX_MAPS_API_KEY = process.env.REACT_APP_YANDEX_MAPS_AK || '';

const normalizeIcon = (icon: any): { url: string; width: number; height: number } | null => {
  if (!icon) return null;

  if (typeof icon === 'string') return { url: icon, width: 32, height: 32 };

  if (icon.url) {
    const w =
      (icon.scaledSize && (icon.scaledSize.width ?? icon.scaledSize?.toString?.().width)) ||
      icon.width ||
      32;
    const h =
      (icon.scaledSize && (icon.scaledSize.height ?? icon.scaledSize?.toString?.().height)) ||
      icon.height ||
      32;
    return { url: icon.url, width: Number(w) || 32, height: Number(h) || 32 };
  }
  return null;
};

const toLonLat = (obj: any): [number, number] => {
  if (obj && typeof obj === 'object') {
    const lon = Number(obj.longitude ?? obj.lng);
    const lat = Number(obj.latitude ?? obj.lat);
    if (Number.isFinite(lon) && Number.isFinite(lat)) return [lon, lat];
  }
  return [37.618423, 55.751244];
};

export const AppointmentMap: FC<IAppointmentMapProps> = ({
  latitude,
  longitude,
  storeType,
  ...props
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('xl'));
  const [scrollY, setScrollY] = useState('');
  const mapRef = useRef<any>(null);

  const { isLoaded, isError, errorMessage } = useYandexMapsScript(YANDEX_MAPS_API_KEY);
  const [mapComponents, setMapComponents] = useState<any>(null);
  const { mapStyles } = useMapStyles();

  useEffect(() => {
    let isMounted = true;

    if (isLoaded && !mapComponents) {
      initializeYandexMaps()
        .then((modules) => {
          if (isMounted) {
            setMapComponents(modules);
          }
        })
        .catch((error) => {
          if (isMounted) {
            console.error('Failed to initialize Yandex Maps:', error);
          }
        });
    }

    return () => {
      isMounted = false;
    };
  }, [isLoaded, mapComponents]);

  useEffect(() => {
    return () => {
      cleanupYandexMaps();
    };
  }, []);

  useEffect(() => {
    if (!isDesktop) return;
    const onScroll = () => setScrollY(window.scrollY > 120 ? 'scrolled' : 'not-scrolled');
    document.addEventListener('scroll', onScroll);
    onScroll();
    return () => document.removeEventListener('scroll', onScroll);
  }, [isDesktop]);

  const hasCoords = Number.isFinite(latitude) && Number.isFinite(longitude);

  const center = useMemo<[number, number]>(() => {
    if (hasCoords) return [Number(longitude), Number(latitude)];
    return toLonLat(DEFAULT_CENTER);
  }, [hasCoords, latitude, longitude]);

  const icon = useMemo(
    () => normalizeIcon(storeType ? getMarkerIcon({ type: storeType }) : null),
    [storeType],
  );

  const wrapperClassName = cx(classes.mapWrapper, {
    [classes.mapWrapperScrolled]: scrollY === 'scrolled',
  });

  const showLoading = (!isLoaded && !isError) || !mapComponents;
  const showError =
    isError ||
    (mapComponents &&
      (!mapComponents.YMap ||
        !mapComponents.YMapDefaultSchemeLayer ||
        !mapComponents.YMapDefaultFeaturesLayer ||
        !mapComponents.YMapMarker));

  if (showLoading || showError) {
    return (
      <div className={wrapperClassName} data-testid="yandex-map-wrapper">
        {showLoading && <MapPlaceholder loading />}
        {showError && <MapPlaceholder error message={errorMessage || 'Failed to load map'} />}
      </div>
    );
  }

  const { YMap, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer, YMapMarker } = mapComponents;

  return (
    <div className={wrapperClassName} data-testid="yandex-map-wrapper">
      <YMap
        ref={mapRef}
        location={{ center, zoom: CHILD_ZOOM }}
        mode="vector"
        {...MAP_OPTIONS}
        {...props}
      >
        <YMapDefaultSchemeLayer customization={mapStyles} />
        <YMapDefaultFeaturesLayer />

        {hasCoords && icon && (
          <YMapMarker coordinates={[Number(longitude), Number(latitude)]}>
            <img
              src={icon.url}
              alt="store marker"
              width={icon.width}
              height={icon.height}
              draggable={false}
              style={{ transform: 'translate(-50%, -100%)' }}
            />
          </YMapMarker>
        )}
      </YMap>
      <div className={classes.veevaNumber}>{v2025PP18603}</div>
    </div>
  );
};
